import { Box, Button, Card, Container, IconButton, Input, InputAdornment, Menu, MenuItem, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldLandmark, Organization } from "src/models";
import useModelData, { replaceById, deleteById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import SimpleDialog from "src/components/simple-dialog";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import DisplayErrors from "src/components/display-errors";
import { formatAddress } from "src/utils/address-utils";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { EldLandmarkModal } from "./eld-landmark-modal";
import { EldLandmarkMapModal } from "./eld-landmark-map-modal";
import Label from "src/components/label";

type EldLandmarksProps = {
  eldLandmarks?: EldLandmark[],
  onEldLandmarkSelected: (a: EldLandmark) => void,
  onEldLandmarkMapSelected: (a: EldLandmark) => void,
  onEldLandmarkDeleteSelected: (a: EldLandmark) => void
}

const EldLandmarks = memo(({ eldLandmarks, onEldLandmarkSelected, onEldLandmarkMapSelected, onEldLandmarkDeleteSelected }: EldLandmarksProps) => {
  const { sortedList, sortData } = useSort(eldLandmarks);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["name"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Landmarks" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell align="left" />

              <SortableHeader sortKey="name" sortData={sortData} >
                Landmark Name
              </SortableHeader>

              <TruxTableCell>
                Address
              </TruxTableCell>
              <TruxTableCell align="right">
                Area
              </TruxTableCell>
              <TruxTableCell align="right">
                Active
              </TruxTableCell>
              <TruxTableCell align="right" />
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldLandmark, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldLandmarkSelected(eldLandmark)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell align="left">
                <IconButton color="error" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEldLandmarkDeleteSelected(eldLandmark);
                }}>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </TruxTableCell>

              <TruxTableCell>
                {eldLandmark.name}
              </TruxTableCell>

              <TruxTableCell>
                {formatAddress(eldLandmark.mainAddress)}
              </TruxTableCell>

              <TruxTableCell align="right">
                <IconButton color="info" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEldLandmarkMapSelected(eldLandmark);
                }}>
                  <Iconify icon="solar:point-on-map-perspective-bold-duotone" />
                </IconButton>
              </TruxTableCell>

              <TruxTableCell align="right">
                {eldLandmark.active && <Label variant="filled" color="success" sx={{ cursor: "pointer" }}>Active</Label>}
                {!eldLandmark.active && <Label variant="filled" color="error" sx={{ cursor: "pointer" }}>Inactive</Label>}
              </TruxTableCell>
              
              <TruxTableCell align="right">
                <EldLandmarkMoreButton eldLandmark={eldLandmark} />
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

function EldLandmarkMoreButton({eldLandmark}: {eldLandmark: EldLandmark}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareFor, setShareFor] = useState("1");
  const [shareLink, setShareLink] = useState();

  return <Box onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}>
    <IconButton onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();

      handleClick(e);
    }}>
      <Iconify icon="solar:menu-dots-bold" rotate={45} />
    </IconButton>
    {open && <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      <MenuItem onClick={(e) => {setShareLink(undefined); setShareFor("1"); setDialogOpen(true); e.preventDefault(); e.stopPropagation(); handleClose();}}>Share Location</MenuItem>
    </Menu>}

    {dialogOpen && <SimpleDialog
      title="Share Location"
      open={dialogOpen}
      onCancel={() => {
        setDialogOpen(false);
      }}
      onSubmit={shareLink ? undefined : () => {
        setLoading(true);

        axios.post(endpoints.eldLandmarks.createShareLink, {
          eldLandmarkId: eldLandmark.id,
          durationDays: Number(shareFor),
        }).then((response) => {
          console.log(response);
          setShareLink(response.data.shareLink);
        }).finally(() => {
          setLoading(false);
        });
      }}
      submitDisabled={loading}
      submitButton="Share Location"
      submitButtonIcon={<Iconify icon="iconamoon:location-duotone" />}
      submitButtonColor="info">

      <Stack direction="column" gap={1}>
        {shareLink
          ? <TruxFormField label="Share Link">
              <Input
              sx={{width: "100%"}}
                type="text"
                disabled
                value={window.location.origin + shareLink}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(window.location.origin + shareLink);
                          enqueueSnackbar("Copied the Share Link");
                        } catch (err) {
                          enqueueSnackbar("Share Link could not be coppied", { variant: 'warning' });
                        }
                      }}
                    >
                      <Iconify icon="solar:copy-linear" />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </TruxFormField>
          : <TruxFormField label="Share For">
            <TruxSimpleSelectField value={shareFor} onChange={(val) => {
              setShareFor(val);
            }}>
              <MenuItem value="1">1 Day</MenuItem>
              <MenuItem value="7">7 Days</MenuItem>
              <MenuItem value="31">31 Days</MenuItem>
            </TruxSimpleSelectField>
          </TruxFormField>
        }
      </Stack>
    </SimpleDialog>}
  </Box>
}

type PageModel = {
  eldLandmarks?: EldLandmark[],
  organizations: Organization[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldLandmarks: axios.get(endpoints.eldLandmarks.findAll).then(response => response.data),
    organizations: axios.get(endpoints.eldOrganizations.childOrganizations).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const selectedEldLandmarkModel = useModelSavingData<EldLandmark>(
    endpoints.eldLandmarks.update,
    endpoints.eldLandmarks.create
  );

  const updateEldLandmark = () => {
    // let's validate the address

    selectedEldLandmarkModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Landmark");
        const eldLandmark = response.data as EldLandmark;
        updateData("eldLandmarks")(replaceById(data!.eldLandmarks!, eldLandmark));

        setIsOpen(false);
      }
    })
  }

  const onEldLandmarkSelected = useCallback((a: EldLandmark) => {
    selectedEldLandmarkModel.set(a);
    setIsOpen(true);
  }, [selectedEldLandmarkModel, setIsOpen]);

  const onEldLandmarkMapSelected = useCallback((a: EldLandmark) => {
    selectedEldLandmarkModel.set(a);
    setIsMapOpen(true);
  }, [selectedEldLandmarkModel, setIsMapOpen]);

  const onEldLandmarkDeleteSelected = useCallback((a: EldLandmark) => {
    selectedEldLandmarkModel.set(a);
    setIsDeleteOpen(true);
  }, [selectedEldLandmarkModel, setIsDeleteOpen]);


  const onNewEldLandmarkSelected = useCallback(() => {
    selectedEldLandmarkModel.set({
      id: 0,
      createdAt: undefined,
      active: true,
      organizationId: 0,
      landmarkType: "",

      name: "",
      mainAddress: {
        attention: "",
        street1: "",
        street2: "",
        city: "",
        state: "",
        zip: "",
      },
      latLoc: 0,
      longLoc: 0,
      appliesToAll: true,
      deviceIds: [],
      polygons: [],
      circles: [],
      emails: [""],
      notifyType: 2,
      appliesToDeviceType: [-2],
      appliesToOrganizationIds: [],
      appliesToDeviceIds: [],
      reportDormancy: false,
      dormancyDurationDays: 7,
    });
    setIsOpen(true);
  }, [selectedEldLandmarkModel]);

  const newEldLandmarkButton = <Button
    onClick={onNewEldLandmarkSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Landmark
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldLandmarks?.length !== 0 &&
          <PageHeader title="Landmarks">
            {newEldLandmarkButton}
          </PageHeader>
        }

        {data && data?.eldLandmarks?.length === 0 &&
          <NoneFound title="No Landmarks Found" message="To add a Landmark click the 'New Landmark' button below.">
            {newEldLandmarkButton}
          </NoneFound>
        }

        {data && data?.eldLandmarks?.length !== 0 && <EldLandmarks eldLandmarks={data.eldLandmarks} onEldLandmarkSelected={onEldLandmarkSelected} onEldLandmarkMapSelected={onEldLandmarkMapSelected} onEldLandmarkDeleteSelected={onEldLandmarkDeleteSelected} />}

        {data && <EldLandmarkModal
          selectedEldLandmarkModel={selectedEldLandmarkModel}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          organizations={data.organizations}
          updateEldLandmark={updateEldLandmark}
        />}


        {data && <EldLandmarkMapModal
          selectedEldLandmarkModel={selectedEldLandmarkModel}
          isOpen={isMapOpen}
          setIsOpen={setIsMapOpen}
          updateEldLandmark={updateEldLandmark}
        />}


        {data && <SimpleDialog
          title="Delete Landmark"
          open={isDeleteOpen}
          onCancel={() => setIsDeleteOpen(false)}
          onSubmit={() => {
            selectedEldLandmarkModel.update(l => {l.active = false});

            selectedEldLandmarkModel.save().then(() => {
              enqueueSnackbar("Successfully deleted the Landmark");
              updateData("eldLandmarks")(deleteById(data.eldLandmarks!, selectedEldLandmarkModel.model!));
              setIsDeleteOpen(false);
            });

          }}
          submitDisabled={selectedEldLandmarkModel.loading}
          submitButton="Delete Landmark"
          submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          submitButtonColor="error">

          <DisplayErrors savingData={selectedEldLandmarkModel} />

          Are you sure you would like to delete this Landmark?
        </SimpleDialog>}

      </DisplayPageData>
    </Container>
  );
}
